import { useEffect, useState } from 'react';

import { DataView } from 'primereact/dataview';
import { Button } from 'primereact/button';

import { ToCurrency } from '../../utils/string-helper';
import { DataTable, TableDefinition } from '../../components/datatable';
import { BillingItem } from '../../models/billing/billing-item';

type ItemsByLoan = {
  loanNumber: string;
  isExpanded: boolean;
  items: BillingItem[];
};

const BillingByLoan = (props: { loading: boolean; items: BillingItem[] }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [groups, setGroups] = useState<ItemsByLoan[]>([]);

  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  useEffect(() => {
    const groupsRecord = props.items.reduce((groups: Record<string, BillingItem[]>, value) => {
      if (!value.loanNumber) return groups;

      if (!groups[value.loanNumber]) {
        groups[value.loanNumber] = [value];
      } else {
        groups[value.loanNumber].push(value);
      }
      return groups;
    }, {});
    const _groups = Object.entries(groupsRecord).map((group) => {
      return { loanNumber: group[0], isExpanded: false, items: group[1] };
    });
    setGroups(_groups);
  }, [props.items]);

  const tableDef: TableDefinition<BillingItem> = {
    columns: [
      {
        field: 'service',
        header: 'Service',
        dataType: 'text',
        filter: false,
        sortable: false,
      },
      {
        field: 'timestamp',
        header: 'Date',
        dataType: 'date',
        filter: false,
        sortable: false,
      },
      {
        field: 'amount',
        header: 'Amount',
        dataType: 'currency',
        filter: false,
        sortable: false,
      },
    ],
  };

  const onToggle = (entry: ItemsByLoan) => {
    const index = groups.findIndex((item) => item.loanNumber === entry.loanNumber);

    entry.isExpanded = !entry.isExpanded;

    const _groups = [...groups];
    _groups[index] = entry;
    setGroups(_groups);
  };

  const itemTemplate = (itemsByLoan: ItemsByLoan) => {
    const icon = itemsByLoan.isExpanded ? 'pi pi-angle-down' : 'pi pi-angle-right';

    return (
      <div className="col-12 py-3">
        <div className="flex flex-row font-bold">
          <div className="flex flex-grow-1 align-items-center">
            <Button
              className="mr-1"
              icon={icon}
              rounded
              text
              onClick={() => onToggle(itemsByLoan)}
              pt={{
                icon: { className: 'text-black' },
              }}
            />
            <span>Loan Number: {itemsByLoan.loanNumber}</span>
          </div>
          <div className="flex justify-content-end gap-8">
            <span>Total Requests: {itemsByLoan.items.length}</span>
            <span>
              Total Cost: {ToCurrency(itemsByLoan.items.reduce((sum: number, value) => sum + (value.amount ?? 0), 0))}
            </span>
          </div>
        </div>
        {itemsByLoan.isExpanded && expansionTemplate(itemsByLoan.items)}
      </div>
    );
  };

  const expansionTemplate = (items: BillingItem[]) => {
    return (
      <div className="col-12 px-3 pt-3">
        <DataTable
          headerClassName="text-gray-400 text-[0.925rem]"
          loading={loading}
          tableDef={tableDef}
          data={items}
          paginator={items.length > 10}
          sortMode="single"
          sortField="loanNumber"
        />
      </div>
    );
  };

  return <DataView value={groups} itemTemplate={itemTemplate} paginator={groups.length > 10} rows={10} />;
};

export default BillingByLoan;
